import { Global, css } from "@emotion/react";
import { BasicModal, Box, Theme, Typography } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import {
  OsUppyFile,
  AllowedSources as UppyAllowedSources,
  UppyComponent,
  UppyType,
} from "@outschool/ui-uppy";
import { useIsomorphicLayoutEffect } from "@outschool/ui-utils";
import React from "react";

import { useUpdateFilestackMetadataMutation } from "../hooks/useUpdateFilestackMetadata";
import { reformatUppyFile } from "../lib/util";

import type {
  PickerResponse,
  PickerFileMetadata,
  PickerOptions,
} from "filestack-js";

const FILESTACK_APP_KEY = (global as any).OUTSCHOOL_SERVER.FILESTACK_APP_KEY;
const SECURED_FILESTACK_APP_KEY = (global as any).OUTSCHOOL_SERVER
  .SECURED_FILESTACK_APP_KEY;
const SECURED_FILESTACK_APP_STORE_PICK_POLICY_2032_08_15 = (global as any)
  .OUTSCHOOL_SERVER.SECURED_FILESTACK_APP_STORE_PICK_POLICY_2032_08_15;
const SECURED_FILESTACK_APP_STORE_PICK_SIGNATURE_2032_08_15 = (global as any)
  .OUTSCHOOL_SERVER.SECURED_FILESTACK_APP_STORE_PICK_SIGNATURE_2032_08_15;
/**
 * From: https://filestack.github.io/filestack-js/interfaces/pickeroptions.html#fromsources
 */

type FilestackSources = "local_file_system" | "webcam" | "unsplash" | "video";

const ALLOWED_FILE_TYPES = {
  // keep in sync with lang-js/apps/media/src/services/imageProcessor.ts
  images: [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
    "image/gif",
    "image/tiff",
    "image/bmp",
    "image/heic",
    "image/heif",
    "image/svg+xml",
    "image/NEF",
    "image/x-adobe-dng",
  ],
  // top 9 video formats in our db
  // after these, the number of videos for other formats are in double digits
  videos: [
    "video/mp4",
    "video/quicktime",
    "video/webm",
    "video/x-ms-wmv",
    "video/x-matroska",
    "video/mpeg",
    "video/avi",
    "video/x-m4v",
    "video/3gpp",
  ],
  others: ["*/*"],
};

export function ImageUpload({
  renderButton,
  cropRatio,
  onChange,
  maxSize = 5 * 1024 * 1024,
  fromSources = ["local_file_system", "unsplash", "webcam"],
  onFileAdded = async () => {},
  uppyAllowedSources = "all",
}: {
  renderButton: (onPick: () => void) => React.ReactNode;
  cropRatio?: number;
  onChange: (file: FilestackFile) => void;
  maxSize?: number;
  fromSources?: FilestackSources[];
  uppyAllowedSources?: UppyAllowedSources;
  onFileAdded?: (file: OsUppyFile) => void;
}) {
  const handleUploaded = React.useCallback(
    (files: FilestackFile[]) => {
      onChange(files[0]);
    },
    [onChange],
  );
  return (
    <FileUploadModal
      renderButton={renderButton}
      cropRatio={cropRatio}
      accept={["image/*"]}
      fromSources={fromSources}
      uppyAllowedSources={uppyAllowedSources}
      maxSize={maxSize}
      maxFiles={1}
      allowedFileTypes={ALLOWED_FILE_TYPES.images}
      onUploaded={handleUploaded}
      onFileAdded={onFileAdded}
      onFilesUploaded={(files: OsUppyFile[]) => {
        const converted = files.map((file: OsUppyFile) =>
          reformatUppyFile({
            uppyFile: file,
            source: file.source ?? "local_storage",
          }),
        );
        return handleUploaded(converted);
      }}
    />
  );
}

export function PrivateAttachmentUpload({
  renderButton,
  onFilesUploaded,
  allowedFileTypes,
  maxFiles = 5,
  uppyAllowedSources = "filesystem",
  cropRatio,
  maxSize = 50 * 1024 * 1024,
}: {
  renderButton: (onPick: () => void) => React.ReactNode;
  onFilesUploaded: (files: OsUppyFile[]) => void;
  allowedFileTypes: string[];
  maxFiles?: number;
  uppyAllowedSources?: UppyAllowedSources;
  cropRatio?: number;
  maxSize?: number;
}) {
  const clientOptions = {
    security: {
      policy: SECURED_FILESTACK_APP_STORE_PICK_POLICY_2032_08_15,
      signature: SECURED_FILESTACK_APP_STORE_PICK_SIGNATURE_2032_08_15,
    },
  };
  return (
    <FileUploadModal
      renderButton={renderButton}
      uppyAllowedSources={uppyAllowedSources}
      maxSize={maxSize}
      maxFiles={maxFiles}
      onFilesUploaded={onFilesUploaded}
      usePrivateUpload={true}
      allowedFileTypes={allowedFileTypes}
      onFileAdded={() => {}}
      cropRatio={cropRatio}
      apiKey={SECURED_FILESTACK_APP_KEY}
      clientOptions={clientOptions}
    />
  );
}

interface AttachmentUploadBaseProps {
  onUploaded: (files: FilestackFile[]) => void;
  accept?: string | string[];
  maxFiles?: number;
  fromSources?: FilestackSources[];
  uppyAllowedSources?: UppyAllowedSources;
  cropRatio?: number;
  maxSize?: number;
  onFileAdded?: (file: OsUppyFile) => void;
}

interface AttachmentUploadWithButtonProps extends AttachmentUploadBaseProps {
  renderButton: (onPick: () => void) => React.ReactNode;
  isOpen?: never;
  setIsOpen?: never;
}

interface AttachmentUploadWithoutButtonProps extends AttachmentUploadBaseProps {
  renderButton?: never;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export function AttachmentUpload({
  onUploaded = () => {},
  accept,
  maxFiles = 5,
  fromSources = ["local_file_system"],
  uppyAllowedSources = "all",
  cropRatio,
  maxSize = 50 * 1024 * 1024,
  renderButton,
  isOpen,
  setIsOpen,
  onFileAdded,
}: AttachmentUploadWithButtonProps | AttachmentUploadWithoutButtonProps) {
  return (
    <FileUploadModal
      fromSources={fromSources}
      uppyAllowedSources={uppyAllowedSources}
      maxSize={maxSize}
      maxFiles={maxFiles}
      onUploaded={onUploaded}
      accept={accept}
      cropRatio={cropRatio}
      allowedFileTypes={ALLOWED_FILE_TYPES.others}
      onFileAdded={onFileAdded}
      onFilesUploaded={(files: OsUppyFile[]) => {
        const converted = files.map((file: OsUppyFile) =>
          reformatUppyFile({
            uppyFile: file,
            source: file.source ?? "local_storage",
          }),
        );
        return onUploaded(converted);
      }}
      {...(renderButton !== undefined
        ? {
            renderButton: renderButton!,
          }
        : {
            isOpen: isOpen,
            onClose: () => setIsOpen(false),
          })}
    />
  );
}

interface VideoUploadBaseProps {
  onUploaded: (file: FilestackFile) => void;
  onFileAdded?: (file: OsUppyFile) => void;
  fromSources?: FilestackSources[];
  uppyAllowedSources?: UppyAllowedSources;
  startUploadingWhenMaxFilesReached?: boolean;
  onError?: (e: string) => void;
}

interface VideoUploadWithButtonProps extends VideoUploadBaseProps {
  renderButton: (onPick: () => void) => React.ReactNode;
  isOpen?: never;
  setIsOpen?: never;
}

interface VideoUploadWithoutButtonProps extends VideoUploadBaseProps {
  renderButton?: never;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export function VideoUpload({
  renderButton,
  fromSources = ["local_file_system", "video"],
  startUploadingWhenMaxFilesReached = false,
  onUploaded,
  onFileAdded,
  uppyAllowedSources,
  onError = e => console.log(e),
  isOpen,
  setIsOpen,
}: VideoUploadWithButtonProps | VideoUploadWithoutButtonProps) {
  const { t } = useTranslation("ui-components-classroom\\FilestackUpload");
  const handleUploaded = React.useCallback(
    (files: FilestackFile[]) => {
      const file = files[0];
      if (file.size === 0) {
        onError(t("File size is zero"));
      } else {
        onUploaded(file);
      }
    },
    [onUploaded, onError, t],
  );

  return (
    <FileUploadModal
      accept={["video/*"]}
      fromSources={fromSources}
      uppyAllowedSources={uppyAllowedSources}
      maxSize={1000 * 1024 * 1024}
      maxFiles={1}
      allowedFileTypes={ALLOWED_FILE_TYPES.videos}
      onUploaded={handleUploaded}
      onFileAdded={onFileAdded}
      onFilesUploaded={(files: OsUppyFile[]) => {
        if (!files || !files[0]) {
          return;
        }
        return onUploaded(
          reformatUppyFile({
            uppyFile: files[0],
            source: files[0].source ?? "local_storage",
          }),
        );
      }}
      startUploadingWhenMaxFilesReached={startUploadingWhenMaxFilesReached}
      {...(renderButton !== undefined
        ? {
            renderButton: renderButton!,
          }
        : {
            isOpen: isOpen,
            onClose: () => setIsOpen(false),
          })}
    />
  );
}

export interface FilestackFile {
  url: string;
  size: number;
  handle?: string;
  source: string;
  status: string;
  filename: string;
  mimetype: string;
  originalPath: string;
  key: string;
}

interface FileUploadModalBaseProps {
  onUploaded?: (files: FilestackFile[]) => void;
  onFileAdded?: (file: OsUppyFile, uppy: UppyType) => void;
  onFilesUploaded?: (files: OsUppyFile[]) => void;
  usePrivateUpload?: boolean;
  allowedFileTypes: string[];
  cropRatio?: number;
  accept?: string | string[];
  fromSources?: FilestackSources[];
  uppyAllowedSources?: UppyAllowedSources;
  maxSize: number;
  maxFiles: number;
  startUploadingWhenMaxFilesReached?: boolean;
  onFileSelected?: any;
  apiKey?: string;
  clientOptions?: Object;
}

interface FileUploadModalWithButtonProps extends FileUploadModalBaseProps {
  renderButton: (openModal: () => void) => React.ReactNode;
  isOpen?: never;
  onClose?: never;
}

interface FileUploadModalWithoutButtonProps extends FileUploadModalBaseProps {
  renderButton?: never;
  isOpen: boolean;
  onClose: () => void;
}

function FileUploadModal({
  onUploaded = () => {},
  onFileAdded,
  onFilesUploaded,
  cropRatio,
  accept,
  fromSources = ["local_file_system"],
  uppyAllowedSources = "all",
  maxSize,
  maxFiles,
  allowedFileTypes,
  startUploadingWhenMaxFilesReached = false,
  onFileSelected,
  apiKey = FILESTACK_APP_KEY,
  // todo(kunal): remove this when we clean up filestack, rn cleaning up one
  // thing leads to something else being unused - so do cleanup together later
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  clientOptions = {},
  renderButton,
  isOpen,
  onClose,
  usePrivateUpload = false,
}: FileUploadModalWithButtonProps | FileUploadModalWithoutButtonProps) {
  const { t } = useTranslation("ui-components-classroom\\FilestackUpload");
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const { updateFilestackMetadata } = useUpdateFilestackMetadataMutation();

  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const actionOptions: PickerOptions = {
    fromSources,
    maxSize,
    maxFiles,
    onFileSelected,
    startUploadingWhenMaxFilesReached,
    onClose,
    allowManualRetry: true,
    onUploadDone: async (result: PickerResponse) => {
      for (const r of result.filesUploaded ?? []) {
        if (!r.key) {
          OsPlatform.captureMessage("Filestack key is missing after upload", {
            extra: {
              fileMetadata: r,
            },
          });
          continue;
        }
        await updateFilestackMetadata(r.handle, r.key);
      }
      if (result.filesUploaded.length > 0) {
        onUploaded(result.filesUploaded as FilestackFile[]);
      }
    },
    onFileUploadFailed: (result: PickerFileMetadata, error: Error) => {
      OsPlatform.captureError(error, {
        extra: {
          fileMetadata: result,
        },
      });
      setErrorMessage(
        t("An error occurred uploading file. Please try again later."),
      );
    },
  };
  if (!!accept) {
    actionOptions.accept = accept;
  }
  if (cropRatio) {
    actionOptions.imageMin = [750, 395];
    actionOptions.transformations = {
      crop: {
        aspectRatio: cropRatio,
        force: true,
      },
      rotate: true,
    };
  }

  // useIsomorphicLayoutEffect so that this works on SSR if it's ever used there
  useIsomorphicLayoutEffect(() => {
    const to = setTimeout(() => {
      const label = document.querySelector(
        'div[title="Unsplash"] > span[class="fsp-source-list__label"]',
      );
      if (label) {
        label.innerHTML = t("Image Search");
      }
    }, 300);
    return () => clearTimeout(to);
  });

  const handleOpenModal = () => {
    setErrorMessage(null);
    setIsModalOpen(true);
  };

  let uppyHeight = 550;
  let uppyWidth = 750;

  return (
    <>
      {renderButton && renderButton(handleOpenModal)}
      <BasicModal
        open={isOpen !== undefined ? isOpen : isModalOpen}
        onClose={onClose !== undefined ? onClose : () => setIsModalOpen(false)}
        hasCloseButton
        closeButtonArialLabel={t("Close upload modal")}
        modalContentProps={{
          sx: {
            padding: 0,
          },
        }}
        modalCloseButtonProps={{
          sx: (theme: Theme) => ({
            zIndex: theme.zIndex.modal + 1,
          }),
        }}
      >
        {!apiKey || apiKey === "test" ? (
          <Box sx={{ padding: 24 }}>
            <Typography variant="body1">
              {t(
                "File uploader is not configured. Please reload this page and try again.",
              )}
            </Typography>
          </Box>
        ) : errorMessage !== null ? (
          <Box sx={{ padding: 24 }}>
            <Typography variant="body1">{errorMessage}</Typography>
          </Box>
        ) : (
          <Box sx={{ marginTop: "35px" }}>
            <UppyComponent
              height={uppyHeight}
              width={uppyWidth}
              maxNumberOfFiles={maxFiles}
              allowedFileTypes={allowedFileTypes}
              onFileAdded={onFileAdded}
              onFilesUploaded={onFilesUploaded}
              cropRatio={cropRatio}
              allowedSources={uppyAllowedSources as UppyAllowedSources}
              usePrivateUpload={usePrivateUpload}
              onSuccess={() => {
                // Close the modal when upload is successful
                if (onClose !== undefined) {
                  onClose();
                } else {
                  setIsModalOpen(false);
                }
              }}
            />
          </Box>
        )}
      </BasicModal>
      <GlobalFilestackTweaksStyle />
    </>
  );
}

const GlobalFilestackTweaksStyle = () => (
  <Global
    styles={css`
      .fsp-picker--inline {
        z-index: 1;
      }
      /*
        From https://github.com/filestack/filestack-js/issues/335#issuecomment-657988398
        This style definition positions the "less than X MB" error at the top of the
        screen, rather than the top of the page, which may be out-of-view
      */
      .fsp-notifications__container {
        position: fixed !important;
      }
    `}
  />
);
